import React from 'react';
import { ArrowLeft, Shield, Eye, Lock, Database, Trash2, Bot, Activity, Webhook } from 'lucide-react';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
            <p className="text-blue-800">
              <strong>Last updated:</strong> July 21, 2025 - Enable.AI ("we", "us", or "our") respects your privacy. This policy explains how we collect, use, and protect your information when you use our AI-powered enablement tools and agentic AI platform.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="w-6 h-6 mr-2 text-blue-600" />
              Information We Collect
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Account Information</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Username and email address for account creation</li>
                <li>Password (encrypted using strong one-way hashing, never stored in plain text)</li>
                <li>Account creation date and last login information</li>
                <li>Credit system usage to track access to premium features</li>
                <li>API keys for programmatic access to our services</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">AI Agent Management</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Agent configurations including names, descriptions, and system prompts</li>
                <li>Agent performance data and feedback logs for quality improvement</li>
                <li>Self-healing recommendations and prompt revision history</li>
                <li>Agent usage patterns and analytics data</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Service Usage & Analytics</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Files you upload for content generation (processed temporarily and never stored persistently)</li>
                <li>Generated content (available for download during the session, not saved)</li>
                <li>Feature usage patterns (non-identifiable, for improving user experience and analytics)</li>
                <li>Quality feedback scores and performance metrics for AI agents</li>
                <li>Webhook configuration URLs for real-time notifications</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Database className="w-6 h-6 mr-2 text-blue-600" />
              How We Use Your Information
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Provide and maintain our AI agent management and content generation services</li>
                <li>Process your uploaded files in real time to generate requested content</li>
                <li>Authenticate your account and manage the credit system</li>
                <li>Monitor and improve AI agent performance through self-healing mechanisms</li>
                <li>Generate analytics and insights for agent optimization</li>
                <li>Send webhook notifications for real-time updates on agent status</li>
                <li>Notify you of critical service updates</li>
                <li>Improve functionality, security, and performance</li>
                <li>Prevent misuse or fraudulent behavior</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Bot className="w-6 h-6 mr-2 text-blue-600" />
              AI Agent Data & Self-Healing
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Agent Performance Monitoring</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>We automatically monitor your AI agents for performance issues</li>
                <li>Quality scores and feedback are analyzed to identify underperforming agents</li>
                <li>Self-healing mechanisms may automatically suggest prompt improvements</li>
                <li>Performance trends are tracked to detect declining agent quality</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Prompt Revision History</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>All prompt changes are logged with timestamps and reasoning</li>
                <li>Revision history is stored to track agent evolution over time</li>
                <li>You can review and approve suggested changes before application</li>
                <li>Historical data helps improve future self-healing recommendations</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Analytics & Insights</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Agent performance metrics are aggregated for trend analysis</li>
                <li>Quality score trends help identify patterns in agent behavior</li>
                <li>Issue breakdowns provide insights into common problems</li>
                <li>Usage analytics help optimize agent configurations</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Webhook className="w-6 h-6 mr-2 text-blue-600" />
              Webhook Notifications
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Real-Time Notifications</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>You may configure webhook URLs to receive real-time notifications</li>
                <li>Webhooks are sent for agent status changes, self-healing events, and feedback submissions</li>
                <li>Webhook payloads contain relevant data but never include sensitive information</li>
                <li>Webhook delivery is logged for debugging and reliability monitoring</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Webhook Security</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Webhook URLs are stored securely and encrypted at rest</li>
                <li>Failed webhook deliveries are retried with exponential backoff</li>
                <li>Webhook endpoints should use HTTPS for secure data transmission</li>
                <li>You can disable webhooks at any time through your dashboard</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-blue-600" />
              Data Security & Privacy
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">No Content Storage</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>We do not store any uploaded files or generated content</li>
                <li>All content is processed in-memory during your session and discarded after processing</li>
                <li>Temporary files are used for generation (e.g., PowerPoint) but are auto-deleted shortly after</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Agent Data Protection</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Agent configurations and prompts are stored securely with encryption</li>
                <li>Performance data is anonymized and aggregated for analytics</li>
                <li>Feedback logs are stored with minimal personal information</li>
                <li>All agent data is isolated by customer and user accounts</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Security Measures</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>All data transmission is encrypted using TLS/HTTPS</li>
                <li>Passwords are stored using industry-standard cryptographic hashing (e.g., bcrypt)</li>
                <li>API keys are securely generated and stored with proper access controls</li>
                <li>Access to infrastructure is protected via role-based access controls and secure login</li>
                <li>We perform periodic security reviews and dependency updates</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Trash2 className="w-6 h-6 mr-2 text-blue-600" />
              Data Retention & Deletion
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Data Type</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Uploaded files</td>
                      <td className="border border-gray-300 px-4 py-2">Deleted immediately after processing</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Generated content</td>
                      <td className="border border-gray-300 px-4 py-2">Not stored, only available for download</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Account data</td>
                      <td className="border border-gray-300 px-4 py-2">Retained while account is active</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Agent configurations</td>
                      <td className="border border-gray-300 px-4 py-2">Retained while account is active</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Performance analytics</td>
                      <td className="border border-gray-300 px-4 py-2">Retained for 2 years for trend analysis</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Feedback logs</td>
                      <td className="border border-gray-300 px-4 py-2">Retained for 1 year for quality improvement</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Webhook logs</td>
                      <td className="border border-gray-300 px-4 py-2">Retained for 30 days for debugging</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">System logs</td>
                      <td className="border border-gray-300 px-4 py-2">Minimal logs retained briefly for debugging and abuse prevention</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-700 mt-4">
                You may delete your account and associated data at any time via your profile settings. Agent data and analytics will be permanently deleted upon account deletion.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">AI Processing</h3>
              <p className="text-gray-700 mb-4">
                We use OpenAI's API to generate content from your uploaded files and Anthropic's Claude API for agent evaluation and self-healing.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Your data is only sent to AI providers for real-time processing</li>
                <li>AI providers do not store or train models on your data when used via their APIs</li>
                <li>More details: <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">OpenAI Data Usage Policy</a> and <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Anthropic Privacy Policy</a></li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Hosting Providers</h3>
              <p className="text-gray-700 mb-4">
                We use Vercel (for frontend hosting) and Railway (for backend and API infrastructure).
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>These services provide encrypted connections and isolated deployment environments</li>
                <li>They do not access or persist your uploaded content</li>
                <li>All data is encrypted in transit and at rest</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">You may:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Access and update your account information</li>
                <li>View and manage your AI agents and their configurations</li>
                <li>Review agent performance analytics and feedback history</li>
                <li>Configure or disable webhook notifications</li>
                <li>Export your agent data and analytics</li>
                <li>Delete your account and associated data</li>
                <li>Opt out of non-essential communications (e.g., newsletters)</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, contact us at: <span className="text-blue-600 font-medium">privacy@weenable.ai</span>
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700">
                If you have any questions or concerns about this policy, please contact us at:
              </p>
              <p className="text-blue-600 font-medium mt-2">
                privacy@weenable.ai
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 